import { useState } from "react";
import { Bell, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DocumentSelect } from "./DocumentSelect";
import { PropertySelect } from "./PropertySelect";
import { FormError } from "@/pages/auth/AuthShell";
import { api, errorMessage } from "@/lib/api";

const EMPTY = { title: "", due_date: "", confirmed: true, document_id: null, remind: "none", notes: "" };
const DAYS = [["none", "No reminder"], ["0", "On the day"], ["1", "1 day before"], ["3", "3 days before"], ["7", "1 week before"], ["14", "2 weeks before"], ["30", "30 days before"]];

export const DeadlineDialog = ({ workspace, properties = [], onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, property_id: properties[0]?.id || null });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canRemind = form.confirmed && !workspace.is_sample;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const { remind, ...rest } = form;
    try {
      await api.post(`/workspaces/${workspace.id}/deadlines`, { ...rest, remind_days_before: canRemind && remind !== "none" ? Number(remind) : null });
      setForm({ ...EMPTY, property_id: properties[0]?.id || null }); setOpen(false); onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="deadline-create-button" className="bg-navy hover:bg-navy-2"><Plus className="mr-1.5 h-4 w-4" /> Add deadline</Button>
      </DialogTrigger>
      <DialogContent data-testid="deadline-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Add a deadline</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="d-title">What is due?</Label>
            <Input id="d-title" data-testid="deadline-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} required className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="d-date">Due date</Label>
            <Input id="d-date" type="date" data-testid="deadline-date-input" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} required className="h-11" />
          </div>
          <label className="flex items-start gap-3 rounded-md border p-3">
            <Checkbox checked={form.confirmed} onCheckedChange={(v) => set("confirmed", !!v)} data-testid="deadline-confirmed-checkbox" className="mt-0.5" />
            <span className="text-sm"><span className="font-medium text-navy">I've confirmed this date</span>
              <span className="block text-xs text-slate-500">Only confirmed dates count as deadlines on your dashboard.</span></span>
          </label>
          <div className="space-y-2">
            <Label>Property</Label>
            <PropertySelect properties={properties} value={form.property_id} onChange={(v) => set("property_id", v)} testId="deadline-property-select" />
          </div>
          <div className="space-y-2">
            <Label>Linked document (optional)</Label>
            <DocumentSelect workspaceId={workspace.id} value={form.document_id} onChange={(v) => set("document_id", v)} testId="deadline-document-select" />
          </div>
          <div className="space-y-2">
            <Label>Reminder</Label>
            <Select value={canRemind ? form.remind : "none"} onValueChange={(v) => set("remind", v)} disabled={!canRemind}>
              <SelectTrigger data-testid="deadline-reminder-select" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <p data-testid="deadline-reminder-notice" className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <Bell className="h-4 w-4 shrink-0" />
              {workspace.is_sample ? "Reminders are turned off in the sample workspace."
                : !form.confirmed ? "Confirm the date first to save a reminder. Unconfirmed dates never send reminders."
                : "We'll email workspace members who have upcoming reminders turned on, at about 8am on the reminder date. Overdue alerts follow your notification settings."}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="d-notes">Notes (optional)</Label>
            <Textarea id="d-notes" data-testid="deadline-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </div>
          <FormError message={error} testId="deadline-form-error" />
          <Button type="submit" disabled={busy} data-testid="deadline-save-button" className="h-11 w-full bg-navy hover:bg-navy-2">{busy ? "Saving..." : "Save deadline"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
